import * as Yup from "yup";

type AnyObject = Record<string, any>;

const toCamelCase = (key: string): string => {
  return key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
};

export const transformKeysToCamelCase = (array: AnyObject[]): AnyObject[] => {
  return array.map((obj) => {
    const newObj: AnyObject = {};
    for (const key in obj) {
      const newKey = toCamelCase(key);
      newObj[newKey] = obj[key];
    }

    const result = {
      ...newObj,
      supportLevel: [
        {
          id: 0,
          checked: newObj?.supportLevel?.includes("EMAIL"),
          name: "Email",
          label: "Email",
        },
        {
          id: 1,
          checked: newObj?.supportLevel?.includes("CALL"),
          name: "Call",
          label: "Call",
        },
      ],
      storageLimit: {
        value: newObj?.storageLimits,
        label: newObj?.storageLimits,
      },
      reportingLevel: newObj?.reportingLevel ? "Yes" : "No",
      subHeading: newObj.suitedFor,
    };

    return result;
  });
};

export const getPaidPlans = (plans: any) => {
  const tabs: string[] = [];
  const result = plans?.filter((plan: any) => {
    if (plan?.plan_category === "PAID") {
      tabs.push(plan?.plan_name);
      return true;
    }
    return false;
  });

  const filteredPlans = result.filter((item: any) => !!item);
  const resultPlans = transformKeysToCamelCase(filteredPlans);

  return { plans: resultPlans, tabs };
};

export const getNoCostPlans = (plans: any) => {
  const filteredNoCostPlans = plans?.filter(
    (plan: any) => plan?.plan_category === "NO_COST"
  );

  const resultPlans = transformKeysToCamelCase(filteredNoCostPlans);
  return { plans: resultPlans };
};

export const getTrailPlans = (plans: any) => {
  const tabHeaders: any[] = [
    "7 Days",
    "14 Days",
    "1 Month",
    "3 Months",
    "6 Months",
    "1 Year",
  ];
  const filteredTraillans = plans?.filter(
    (plan: any) => plan?.plan_category === "TRIAL"
  );

  const addingAllTabsData = tabHeaders?.map((tab: any) => {
    const result = filteredTraillans?.find(
      (plan: any) => plan?.plan_type === tab
    );
    if (result) {
      return result;
    }
    return {};
  });

  const resultPlans = transformKeysToCamelCase(addingAllTabsData);

  return { plans: resultPlans };
};

interface SupportLevelOption {
  id: number;
  checked: boolean;
  name: string;
  label: string;
}

export interface PlanValues {
  planName: string;
  subHeading: string;
  notes: string;
  planType: string;
  monthlyPrice: string;
  annualPrice: string;
  noOfUsers: number | null;
  noOfLeads: number | null;
  noOfContacts: number | null;
  storageLimit: number | null;
  apiCallsPerMonth: number | null;
  automationRulesLimit: number | null;
  supportLevel: SupportLevelOption[];
  reportingLevel: boolean;
  planCategory: string;
  themeColor: string;
}

export const initialValues: PlanValues = {
  planName: "",
  subHeading: "",
  notes: "",
  planType: "",
  monthlyPrice: "",
  annualPrice: "",
  noOfUsers: null,
  noOfLeads: null,
  noOfContacts: null,
  storageLimit: null,
  apiCallsPerMonth: null,
  automationRulesLimit: null,
  supportLevel: [
    {
      id: 0,
      checked: false,
      name: "Email",
      label: "Email",
    },
    {
      id: 1,
      checked: false,
      name: "Call",
      label: "Call",
    },
  ],
  reportingLevel: false,
  planCategory: "paid",
  themeColor: "",
};

export const validationSchema = Yup.object().shape({
  planName: Yup.string().required("Plan name is required"),
  subHeading: Yup.string(),
  monthlyPrice: Yup.string()
    .required("Monthly price is required")
    .matches(
      /^(?:\d{0,7})(?:\.\d{1,2})?$/,
      "Enter a valid amount (up to 7 digits and 2 decimals)"
    )
    .test("max-value", "Amount cannot exceed 9,999,999.99", (value) => {
      if (!value) return true;
      return Number(value) <= 9999999.99;
    }),
  annualPrice: Yup.string()
    .required("Yearly price is required")
    .matches(
      /^(?:\d{0,7})(?:\.\d{1,2})?$/,
      "Enter a valid amount (up to 7 digits and 2 decimals)"
    )
    .test("max-value", "Amount cannot exceed 9,999,999.99", (value) => {
      if (!value) return true;
      return Number(value) <= 9999999.99;
    }),
  noOfUsers: Yup.string().required("No of Users is required"),
  noOfLeads: Yup.string().required("No of Leads is required"),
  noOfContacts: Yup.string().required("No of Contacts is required"),
  storageLimit: Yup.object().required("Data Storage Limit is required"),
  apiCallsPerMonth: Yup.string().required("Api Calls/Month is required"),
  automationRulesLimit: Yup.string().required(
    "Custom Automation Rules is required"
  ),
  supportLevel: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        label: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    )
    .test(
      "at-least-one-checked",
      "At least one support level must be selected",
      (value) => value?.some((option) => option.checked)
    ),
});

export const planTypeDropdown = [
  { value: "7 Days", label: "7 Days" },
  { value: "14 Days", label: "14 Days" },
  { value: "1 Month", label: "1 Month" },
  { value: "3 Months", label: "3 Months" },
  { value: "6 Months", label: "6 Months" },
  { value: "1 Year", label: "1 Year" },
];

export const storageDropdown = [
  { value: "1GB", label: "1GB" },
  { value: "2GB", label: "2GB" },
  { value: "5GB", label: "5GB" },
  { value: "10GB", label: "10GB" },
  { value: "50GB", label: "50GB" },
  { value: "100GB", label: "100GB" },
];
